import { Web3Config } from '@types'
import { getAddressesConfig } from '../contracts/config'

// Lazy initialization to avoid SSR issues
let _web3Config: Web3Config | null = null

function createWeb3Config(): Web3Config {
  const addresses = getAddressesConfig()

  return {
    genesisNftConfig: {
      series1: {
        address: addresses.GenesisNFTV1,
      },
      series2: {
        address: addresses.GenesisNFTV2,
      },
    },
    genesisNftStaking: {
      address: addresses.StakingGenesisNFT,
    },
    genesisNftStakingRewards: {
      address: addresses.StakingGenesisNFTVesting,
    },
    ownershipNft: {
      address: addresses.OwnershipNFT,
    },
    ownershipStaking: {
      address: addresses.OwnershipStaking,
    },
    wlth: {
      address: addresses.Wlth,
      decimals: 18,
      contractAddress:
        'https://basescan.org/address/0x99b2B1A2aDB02B38222ADcD057783D7e5D1FCC7D',
    },
    stakingWlth: {
      address: addresses.StakingWlth,
    },
    stakingForDiscount: { address: addresses.StakingForDiscount },
    wlthBonusStaking: { address: addresses.WlthBonusStaking },
    wlthStakingForParticipation: { address: addresses.ParticipationStaking },
    usdc: {
      address: addresses.USDC,
      decimals: 6,
    },
    rpcUrls: (process.env.NEXT_PUBLIC_RPC_URLS || '')
      .replace(/ /g, '')
      .split(',') as string[],
    logsBrowserRpcUrl: process.env.NEXT_PUBLIC_LOGS_BROWSER_RPC_URL || '',
    uniswapConfig: {
      quoterAddress: addresses.UniswapWlthPriceOracle,
      swapFees: {
        wlth_usdc: 3000,
      },
    },
    fundRegistryAddress: addresses.InvestmentFundRegistry,
    vesting: {
      genesisNFTVesting: {
        address: addresses.GenesisNFTVesting,
      },
      stakingGenesisNFTVesting: {
        address: addresses.StakingGenesisNFTVesting,
      },
      simpleVesting: {
        address: addresses.SimpleVesting,
      },
      /*whitelistedVesting: {
        address: addresses.WhitelistedVesting,
      },*/
    },
    // separate vesting
    whitelistedVesting: {
      address: addresses.WhitelistedVesting,
    },
    strategicPartners2: {
      address: addresses.StrategicPartners2,
    },
    marketing: {
      address: addresses.Marketing,
    },
    advisors: {
      address: addresses.Advisors,
    },
    strategicPartners1: {
      address: addresses.StrategicPartners1,
    },
    team: {
      address: addresses.Team,
    },
    chainIds: {
      test: 84532,
      prod: 8453,
    },
    tgeAllocation: {
      strategicPartners: {
        cadenceNumber: 18n,
      },
      marketing: {
        cadenceNumber: 30n,
      },
      advisors: {
        cadenceNumber: 27n,
        availabilityTimestamp: Number.parseInt(
          process.env.NEXT_PUBLIC_TGE_ALLOCATION_ADVISORS_AVAILABILITY_TIMESTAMP || '0',
        ),
      },
      strategicPartners1: {
        cadenceNumber: 18n,
        availabilityTimestamp: Number.parseInt(
          process.env
            .NEXT_PUBLIC_TGE_ALLOCATION_STRATEGIC_PARTNERS_1_AVAILABILITY_TIMESTAMP ||
            '0',
        ),
      },
      team: {
        cadenceNumber: 33n,
        availabilityTimestamp: Number.parseInt(
          process.env.NEXT_PUBLIC_TGE_ALLOCATION_TEAM_AVAILABILITY_TIMESTAMP || '0',
        ),
      },
    },
    marketplace: {
      address: addresses.Marketplace,
      paymentToken: {
        address: addresses.USDC,
        decimals: 6,
      },
    },
    network: process.env.NEXT_PUBLIC_NETWORK as string,
  }
}

function getWeb3Config(): Web3Config {
  if (!_web3Config) {
    _web3Config = createWeb3Config()
  }
  return _web3Config
}

// Lazy-loaded Proxy to avoid SSR issues
export const web3Config = new Proxy({} as Web3Config, {
  get(_target, prop) {
    return getWeb3Config()[prop as keyof Web3Config]
  },
  ownKeys() {
    return Reflect.ownKeys(getWeb3Config())
  },
  has(_target, prop) {
    return prop in getWeb3Config()
  },
  getOwnPropertyDescriptor(_target, prop) {
    const config = getWeb3Config()
    if (prop in config) {
      return {
        enumerable: true,
        configurable: true,
        value: config[prop as keyof Web3Config],
      }
    }
  },
})
